/**
 * 滋賀県 名物めし 12段階（コンテンツデータ）。
 *
 * 数値（radius / mass / score / unlockScore）は他県と同じテンプレート。
 * 12品すべてが農水省「うちの郷土料理」滋賀県の30品から採ってある。
 *
 * 琵琶湖の湖魚が層として厚いので、6品（えび豆・小あゆ・しじみ汁・焼きもろこ・
 * ビワマス刺身・鮒ずし）を湖の幸で組んでいる。ただし全部が茶色い煮物にならないよう、
 * 色を赤・桃・橙・淡黄・銀青・濃茶に散らし、茶色い煮物は3品に抑えた。
 *
 * 鯖の棒ずしは使わない。
 *  京都（Lv12 さばずし）と同じ、棒状に締めて輪切りにする形になる。
 *  どちらも鯖街道の料理だが、街道の行き先である京都に譲った。
 *  かわりに Lv10 をあめのいおご飯（ビワマスの炊き込みご飯）にしている。
 *  ビワマスは Lv9 の刺身と重なるが、生の切り身と炊き込みご飯なので絵はまったく別になる。
 *
 * 丁稚羊羹について。
 *  福井（Lv6 でっち羊かん）と同名だが、福井のものは紙箱に入った薄い水羊羹の板。
 *  滋賀のものは竹の皮に包んで蒸した細長い羊羹で、竹の皮を必ず描くことで分けている。
 *
 * 近江牛について。
 *  牛は他7県で使用済み。8品目になるので、形態を味噌漬に限定した。
 *  味噌の膜をまとった肉を焼いたもので、ステーキの桃色の断面も、すき焼きの割下と溶き卵も、
 *  丼の白飯も、刺身の生の赤も出ない。江戸時代に牛肉を薬として献上した話も滋賀にしかない。
 *
 * 調査で出てきた「書いてはいけないこと」を反映している。
 *  - 赤こんにゃくが赤い理由を断定しない。農水省は「由来や起源を示す史料はなく、
 *    赤い理由はわからないままだ」と明記している。信長説を事実として書かない。
 *  - 琵琶湖は「日本最大の淡水湖」。県公式の文言どおり。「日本最大の湖」と短縮しない。
 *  - 小あゆを「固有種」と書かない。農水省に明記がない。固有種はビワマス・セタシジミ・ホンモロコ。
 *  - 近江牛の「日本三大和牛」「日本最古のブランド牛」は伝聞形か未確認。使わない。
 *  - 鮒ずしを「日本最古のすし」と書かない。
 *  - 特定の菓子メーカーの商品名を出さない。
 *
 * 本番画像は public/assets/foods/shiga/ に置く。
 *
 * 2026-10-02 確定案（audit/_確定案.csv）で料理を入れ替えた。
 * - 外した品：えび豆・丁字麩のからしあえ・日野菜漬け・焼きもろこのどろ酢・丁稚羊羹・あめのいおご飯・近江牛の味噌漬（店で食べられる品に差し替え）
 * - 小鮎の天ぷら：小アユを「固有種」と書かない。農水省・県のページに明記がない（既存の shigaFood.ts の方針どおり）
 * - 小鮎の天ぷら：漁期は書かない。農水省は「5月頃から8月上旬」、県（滋賀のおいしいコレクション）は収穫時期「1～8月上旬」で食い違うため
 * - のっぺいうどん：「発祥の店」「明治◯年に考案」は書かない。公式（国・自治体）で裏が取れない
 * - のっぺいうどん：葛を使うこと、名前の由来は書かない。開いた公式ページに記載がない
 * - 近江ちゃんぽん：発祥の店・開業年（昭和38年）は書かない。店の自己紹介文・ニュース・PR記事にしかなく、国・自治体で裏が取れない
 * - 近江ちゃんぽん：豚肉・キャベツ・もやし・きくらげなどの具の名前、酢をかける食べ方、魚介を使わないことは書かない。開いた公式ページに書かれていない
 * - 近江ちゃんぽん：「滋賀県のご当地B級グルメ」は書かない。びわこビジターズビューロー上の店の自己紹介文にしかない
 * - 焼鯖そうめん：「発祥の店」は書かない
 * - 本もろこの塩焼き：「塩焼き」とは書かない。県・農水省のページは「素焼き」「串に刺して焼いた」で、塩を振ることは書いていない
 * - 鴨鍋：いまも琵琶湖で獲った鴨を使っているとは書かない。琵琶湖が禁猟区になったという情報が検索で出たが、公式ページを開いて確かめられていないため、年も含めて書かない
 * - 近江牛すき焼き：「日本三大和牛」「最古のブランド牛」は書かない（既存 shigaFood.ts の方針どおり。県のページにない）
 * - 近江牛すき焼き：すき焼きの具（焼き豆腐・春菊など）は書かない。県のページにない
 */

import { attachHulls } from '../../utils/itemShape';
import type { RewardLink, StageDefinition, StageItemDefinition } from '../types';

import { shigaSceneryTour } from './shigaScenes';
import { SHIGA_FOOD_HULLS } from './shigaFoodShapes.generated';

/** 「食べに行く」導線の既定リンク（特定店の宣伝にならないよう検索リンクにする）。 */
function searchLinks(area: string, dish: string): RewardLink[] {
  return [
    {
      label: '地図で探す',
      url: `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(`${area} ${dish}`)}`,
    },
    {
      label: 'お店を調べる',
      url: `https://www.google.com/search?q=${encodeURIComponent(`${area} ${dish}`)}`,
    },
  ];
}

const FOOD_ITEMS: StageItemDefinition[] = [
  {
    id: 'aka-konnyaku',
    level: 1,
    name: '赤こんにゃく煮',
    shortName: '赤こんにゃく',
    image: '/assets/foods/shiga/01_aka_konnyaku.webp',
    radius: 17,
    mass: 1,
    score: 2,
    description: '理由のわからない、赤。',
    source:
      'https://www.maff.go.jp/j/keikaku/syokubunka/k_ryouri/search_menu/menu/aka_konnyaku_ni_shiga.html',
    icon: { label: '赤こんにゃく', shape: 'mochi', baseColor: '#c8302a', accentColor: '#8a2018' },
    reward: {
      unlockScore: 200,
      area: '近江八幡市',
      headline: '朱色に染まったこんにゃくを薄く切って煮る、近江八幡のふしぎな赤い一品。',
      tip: '赤こんにゃくは三二酸化鉄という鉄分で赤く染められている。織田信長が染めさせたなど諸説あるが、由来を示す史料はなく、なぜ赤いのかはわかっていない。冠婚葬祭や学校給食に欠かせない品。',
      links: searchLinks('近江八幡市', '赤こんにゃく'),
    },
  },
  {
    id: 'shijimi-jiru',
    level: 2,
    name: 'しじみ汁',
    shortName: 'しじみ汁',
    image: '/assets/foods/shiga/02_shijimi_jiru.webp',
    radius: 21,
    mass: 1.3,
    score: 4,
    description: '湖にしかいない貝の汁。',
    source:
      'https://www.maff.go.jp/j/keikaku/syokubunka/k_ryouri/search_menu/menu/shijimi_jiru_shiga.html',
    icon: { label: 'しじみ汁', shape: 'pot', baseColor: '#2a2a30', accentColor: '#c8b878' },
    reward: {
      unlockScore: 500,
      area: '滋賀県内全域（瀬田川・南湖周辺）',
      headline: '琵琶湖にしかいないセタシジミの汁。殻がふっくら厚く、身にこくがある。',
      tip: 'セタシジミは琵琶湖だけに生息する固有種で、ヤマトシジミやマシジミにくらべて殻がふっくら大きく肉厚。旬は12月から4月で、春がおいしいと地元の漁師は言う。',
      links: searchLinks('大津市', 'セタシジミ'),
    },
  },
  {
    id: 'koayu-tempura',
    level: 3,
    name: '小鮎の天ぷら',
    shortName: '小鮎の天ぷら',
    image: '/assets/foods/shiga/03_koayu_tempura.webp',
    radius: 26,
    mass: 1.7,
    score: 8,
    description: '琵琶湖の小アユは体長7cmほどにしか育たないアユで、食感がやわらかい。洗って塩を振り、片栗粉と、卵・冷水・小麦粉の衣をつけて180度の油で揚げる。揚げたての湖魚は臭みがないとされる。',
    source:
      'https://www.maff.go.jp/j/keikaku/syokubunka/k_ryouri/search_menu/menu/kogyono_tenpura_shiga.html',
    icon: { label: '小鮎天', shape: 'fish', baseColor: '#e0b860', accentColor: '#a8b0b8' },
    reward: {
      unlockScore: 1000,
      area: '琵琶湖沿岸（滋賀県各地）',
      headline: '琵琶湖の小アユを衣で揚げる',
      tip: '琵琶湖の小アユは季節で食べ方が変わる。冬の氷魚は釜揚げ、春先は佃煮、爽やかな苦みが出る初夏は天ぷらが合うとされる。',
      links: searchLinks('滋賀県', '小鮎の天ぷら'),
      official: [{ label: '滋賀のおいしいコレクション（滋賀県）「コアユ」', url: 'https://shigaquo.jp/foods/4825.html' }],
    },
  },
  {
    id: 'koayu-sansho',
    level: 4,
    name: '小あゆの山椒煮',
    shortName: '小あゆ煮',
    image: '/assets/foods/shiga/04_koayu_sansho.webp',
    radius: 30,
    mass: 2.2,
    score: 16,
    description: '七センチのままの鮎。',
    source:
      'https://www.maff.go.jp/j/keikaku/syokubunka/k_ryouri/search_menu/menu/koayuno_sanshou_ni_shiga.html',
    icon: { label: '小あゆ煮', shape: 'fish', baseColor: '#6a4a28', accentColor: '#5a7a38' },
    reward: {
      unlockScore: 1800,
      area: '滋賀県内全域（琵琶湖）',
      headline: '琵琶湖でとれる7センチほどの小さなあゆを、粒山椒と一緒に甘辛く煮ふくめた佃煮。',
      tip: '琵琶湖のあゆは体長7センチほどにしか育たないため小あゆと呼ばれる。川をさかのぼったあゆが水苔を食べて20センチほどになるのとは対照的で、平成10年に滋賀の食文化財に選択された。',
      links: searchLinks('滋賀県', '小あゆの山椒煮'),
    },
  },
  {
    id: 'noppei-udon',
    level: 5,
    name: 'のっぺいうどん',
    shortName: 'のっぺいうどん',
    image: '/assets/foods/shiga/05_noppei_udon.webp',
    radius: 36,
    mass: 2.8,
    score: 32,
    description: 'うどんに、だしの効いたとろみのあんをかけ、大きな椎茸をのせる。長浜の名物として知られる。',
    source:
      'https://kitabiwako.jp/spot/spot_2669',
    icon: { label: 'のっぺい', shape: 'noodle-bowl', baseColor: '#c89a48', accentColor: '#5a3a22' },
    reward: {
      unlockScore: 3000,
      area: '長浜市',
      headline: '大きな椎茸がのるあんかけうどん',
      tip: '長浜米原観光協会は、焼鯖そうめんと同じく長浜の名物として挙げている。',
      links: searchLinks('長浜市', 'のっぺいうどん'),
    },
  },
  {
    id: 'omi-champon',
    level: 6,
    name: '近江ちゃんぽん',
    shortName: '近江ちゃんぽん',
    image: '/assets/foods/shiga/06_omi_champon.webp',
    radius: 42,
    mass: 3.5,
    score: 64,
    description: '昆布とかつお節でとっただしのスープに、野菜をたっぷり合わせた彦根生まれの麺料理。スープはあっさりとしてやさしい味わいで、野菜の量が多いのが持ち味。',
    source:
      'https://trip.hikoneshi.com/en/course_activity.html',
    icon: { label: 'ちゃんぽん', shape: 'ramen', baseColor: '#e6c46a', accentColor: '#6a9a3a' },
    reward: {
      unlockScore: 4500,
      area: '彦根市',
      headline: '昆布とかつお節のだしに野菜たっぷり',
      tip: '彦根市の観光案内では、地元の人のソウルフードとして紹介されている。自家製の麺に国産の野菜を合わせる店もある。',
      links: searchLinks('彦根市', '近江ちゃんぽん'),
    },
  },
  {
    id: 'yakisaba-somen',
    level: 7,
    name: '焼鯖そうめん',
    shortName: '焼鯖そうめん',
    image: '/assets/foods/shiga/07_yakisaba_somen.webp',
    radius: 46,
    mass: 4.4,
    score: 128,
    description: '焼きサバを出汁と調味料で煮込み、その煮汁でそうめんに味をつけて一緒に盛る。若狭から京都へ海産物を運ぶ鯖街道があり、湖北ではサバが手に入りやすかった。',
    source:
      'https://www.maff.go.jp/j/keikaku/syokubunka/k_ryouri/search_menu/menu/saba_soumen_shiga.html',
    icon: { label: '焼鯖そうめん', shape: 'noodle-bowl', baseColor: '#c8a070', accentColor: '#5a2e18' },
    reward: {
      unlockScore: 6500,
      area: '長浜市ほか湖北地域',
      headline: '焼きサバの煮汁でそうめんを煮る',
      tip: '春の農繁期、嫁いだ娘の家へ焼きサバを贈る「五月見舞い」の風習があった。毎年4月の長浜八幡宮の曳山まつりでもふるまわれる。',
      links: searchLinks('長浜市', '焼鯖そうめん'),
      official: [{ label: '長浜米原観光協会「食通を唸らす『北びわこ』を旅しよう」', url: 'https://kitabiwako.jp/highlight/highlight_9074' }],
    },
  },
  {
    id: 'honmoroko-shioyaki',
    level: 8,
    name: '本もろこの塩焼き',
    shortName: '本もろこの塩焼き',
    image: '/assets/foods/shiga/08_honmoroko_shioyaki.webp',
    radius: 51,
    mass: 5.4,
    score: 256,
    description: '琵琶湖のホンモロコは肉質がよく、秋から冬にかけて脂がのる。七輪などで焼くと、滴る脂に火が付くほどとされる。手頃な大きさなので素焼きにして味わい、春が近づくと子持ちのものが出る。',
    source:
      'https://shigaquo.jp/hacchin/concept/honmoroko.html',
    icon: { label: 'もろこ焼き', shape: 'fish', baseColor: '#c8a060', accentColor: '#f0eee6' },
    reward: {
      unlockScore: 9000,
      area: '琵琶湖沿岸（滋賀県各地）',
      headline: '脂ののった湖の小魚を焼く',
      tip: 'ホンモロコは琵琶湖周辺にのみ生息する固有種とされ、「琵琶湖八珍」に選ばれている。絶滅危惧IA類だが、稚魚の放流や養殖で今も食べられる。',
      links: searchLinks('滋賀県', 'もろこ 塩焼き'),
    },
  },
  {
    id: 'biwamasu-sashimi',
    level: 9,
    name: 'ビワマスの刺身',
    shortName: 'ビワマス刺身',
    image: '/assets/foods/shiga/09_biwamasu_sashimi.webp',
    radius: 57,
    mass: 6.6,
    score: 512,
    description: '雨の魚と呼ばれた、湖の宝。',
    source:
      'https://www.maff.go.jp/j/keikaku/syokubunka/k_ryouri/search_menu/menu/biwamasu_sasimi_shiga.html',
    icon: { label: 'ビワマス刺身', shape: 'sashimi-platter', baseColor: '#e89868', accentColor: '#f4f0e8' },
    reward: {
      unlockScore: 12000,
      area: '滋賀県内全域（琵琶湖）',
      headline: '琵琶湖にしかいないビワマスを刺身で。初夏から夏にかけて、いちばん脂がのる。',
      tip: '滋賀県には84種を超える魚類がいて、そのうち16種が琵琶湖だけの固有種。ビワマスはその一つで、産卵期に大雨の日に遡上することからアメノイオとも呼ばれる。',
      links: searchLinks('滋賀県', 'ビワマス'),
    },
  },
  {
    id: 'kamo-nabe',
    level: 10,
    name: '鴨鍋',
    shortName: '鴨鍋',
    image: '/assets/foods/shiga/10_kamo_nabe.webp',
    radius: 63,
    mass: 8,
    score: 1024,
    description: '冬にシベリアから飛来する琵琶湖の鴨は、厳冬期の滋養として古くから食べられてきた。湖北の冬の名物で、鴨のロース肉や胸肉、ミンチを、甘い割下で野菜と一緒に煮て食べる。',
    source:
      'https://shoryudo.go-centraljapan.jp/ja/spot_87.html',
    icon: { label: '鴨鍋', shape: 'hotpot', baseColor: '#b85a48', accentColor: '#5a8a3a' },
    reward: {
      unlockScore: 16000,
      area: '長浜市ほか湖北地域',
      headline: '湖北の冬だけの鴨の鍋',
      tip: '11月15日の鴨猟の解禁とともに、長浜の料理店では冬の名物が始まる。11月中旬から3月までの期間限定で、多くの店が天然の真鴨を使う。',
      links: searchLinks('長浜市', '鴨鍋'),
      official: [{ label: '長浜米原観光協会「鴨料理」', url: 'https://kitabiwako.jp/spot/spot_8474' }],
    },
  },
  {
    id: 'funazushi',
    level: 11,
    name: 'ふなずし',
    shortName: '鮒ずし',
    image: '/assets/foods/shiga/11_funazushi.webp',
    radius: 70,
    mass: 9.6,
    score: 2048,
    description: '正月まで待つ、湖のすし。',
    source:
      'https://www.maff.go.jp/j/keikaku/syokubunka/k_ryouri/search_menu/menu/funa_zushi_shiga.html',
    icon: { label: '鮒ずし', shape: 'roe', baseColor: '#e8e0cc', accentColor: '#e08828' },
    reward: {
      unlockScore: 22000,
      area: '滋賀県内全域（琵琶湖）',
      headline: '子持ちニゴロブナを飯に漬けて数か月発酵させる、滋賀のなれずしの原点。',
      tip: '春先に塩漬けし、夏に洗って干し、桶に飯を敷いて重ならないよう並べ、正月まで数か月漬けこむ。発酵中に生まれる乳酸で骨がやわらかくなり、骨まで食べられるようになる。',
      links: searchLinks('滋賀県', 'ふなずし'),
    },
  },
  {
    id: 'omigyu-sukiyaki',
    level: 12,
    name: '近江牛すき焼き',
    shortName: '近江牛すき焼き',
    image: '/assets/foods/shiga/12_omigyu_sukiyaki.webp',
    radius: 78,
    mass: 11.4,
    score: 4096,
    description: '近江牛は、滋賀県内で最も長く飼育された黒毛和種。霜降りの度合いが高く、芳醇な香りとやわらかさをもつ。県はその味わい方として、ステーキやしゃぶしゃぶと並べてすき焼きを挙げる。',
    source:
      'https://shigaquo.jp/foods/4920.html',
    icon: { label: '近江牛すき', shape: 'hotpot', baseColor: '#c84a4a', accentColor: '#f0e0d0' },
    reward: {
      unlockScore: 30000,
      area: '滋賀県',
      headline: '霜降りの近江牛を鍋で味わう',
      tip: '江戸時代の初期に、彦根藩が牛肉の味噌漬けを将軍家に献上した記録が残っている。',
      links: searchLinks('滋賀県', '近江牛 すき焼き'),
      official: [{ label: '滋賀県「食の魅力」', url: 'https://www.pref.shiga.lg.jp/miryoku/shoku/' }],
    },
  },
];

/** 得点は 2 ^ level を基準にしている（GAME_TUNING.score で方式変更可能）。 */
export const shigaFoodStage: StageDefinition = {
  regionId: 'shiga',
  regionName: '滋賀県',
  mode: 'food',
  title: '滋賀めしマージ',
  theme: {
    primaryColor: '#2a6a8a',
    secondaryColor: '#d8b858',
    accentColor: '#e8d080',
    backgroundColor: '#101820',
  },
  /** 背景ツアー「滋賀めぐり」。軸は季節。春の八幡堀から、冬の延暦寺へ。 */
  scenery: {
    tour: shigaSceneryTour,
  },
  items: attachHulls(FOOD_ITEMS, SHIGA_FOOD_HULLS),
};
